import type { TemplateDefinition } from './types';

/**
 * Add Project Wizard — Thin++ Template (S9-09)
 *
 * Minimal intake form for creating new projects.
 * Captures only the 7-8 fields needed to register a project:
 * 1. title (Device/Project Name in target language)
 * 2. title_original (Original-language name)
 * 3. description
 * 4. description_original
 * 5. domain (one of: biotech, medtech, deeptech, shallowtech)
 * 6. subdomain (conditional — only if domain has subdomains)
 * 7. source_class (founder / academia / corporate / internal / scouting)
 * 8. source_url (where the project came from)
 *
 * Full enrichment (TRL, owner, institution, scoring, etc.) happens
 * in QuickEntry after the project is created in INBOX stage.
 *
 * This template is domain-agnostic; the same 7-8 fields apply to all domains.
 */

export const wizardTemplate: TemplateDefinition = {
  domain: 'biotech', // Placeholder; this template applies to all domains
  version: 1,

  quickInfo: [
    {
      key: 'title',
      label: 'Project Name',
      type: 'text',
      source: 'core',
      required: true,
      width: 'full',
      showInCard: true,
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      source: 'core',
      width: 'full',
      showInCard: true,
    },
  ],

  tabs: [
    {
      id: 'project-basics',
      label: 'Project Basics',
      icon: '📋',
      sections: [
        {
          id: 'identification',
          label: 'Project Identification',
          description: 'Basic information about the project',
          fields: [
            {
              key: 'title',
              label: 'Project Name',
              type: 'text',
              source: 'core',
              required: true,
              placeholder: 'Name in English',
              width: 'full',
              showInTable: true,
              showInCard: true,
            },
            {
              key: 'title_original',
              label: 'Original Name',
              type: 'text',
              source: 'core',
              placeholder: 'Original language name',
              width: 'full',
            },
            {
              key: 'description',
              label: 'Description',
              type: 'textarea',
              source: 'core',
              placeholder: 'What is this project about?',
              width: 'full',
              showInCard: true,
            },
            {
              key: 'description_original',
              label: 'Original Description',
              type: 'textarea',
              source: 'core',
              placeholder: 'Description in original language',
              width: 'full',
            },
          ],
        },
        {
          id: 'classification',
          label: 'Classification & Source',
          description: 'Domain, subdomain, and project source information',
          fields: [
            {
              key: 'domain',
              label: 'Domain',
              type: 'enum',
              source: 'core',
              required: true,
              width: 'half',
              enumValues: [
                { value: 'biotech', label: 'Biotech' },
                { value: 'medtech', label: 'Medtech' },
                { value: 'deeptech', label: 'Deeptech' },
                { value: 'shallowtech', label: 'Shallowtech' },
              ],
            },
            {
              key: 'subdomain',
              label: 'Subdomain',
              type: 'enum',
              source: 'core',
              placeholder: 'Select domain first',
              width: 'half',
              enumValues: [], // Populated dynamically based on domain selection
            },
            {
              key: 'source_class',
              label: 'Source Class',
              type: 'enum',
              source: 'core',
              width: 'half',
              enumValues: [
                { value: 'ACADEMIA', label: 'Academia' },
                { value: 'FOUNDER', label: 'Founder' },
                { value: 'CORPORATE', label: 'Corporate' },
                { value: 'INTERNAL', label: 'Internal' },
                { value: 'SCOUTING', label: 'Scouting' },
              ],
            },
            {
              key: 'source_url',
              label: 'Source URL',
              type: 'url',
              source: 'core',
              placeholder: 'https://...',
              width: 'half',
            },
          ],
        },
      ],
    },
  ],

  requiredForCreation: ['title', 'domain'],
};
